/**
 * IconButton: a square button with only an icon (cockatrice-ui `src/ui/IconButton.jsx`, ported to hyperscript;
 * PLAN.md §0). `label` is required: it becomes the aria-label and the hover title.
 * Pass `pressed` (true/false) for a toggle such as Bold or the notes panel.
 */
import './css/icon-button.css';
import { mergeProps, splitProps } from 'solid-js';
import Icon from './Icon.ts';
import type { IconName } from './Icon.ts';
import { component, el, spread, when } from './solid.ts';
import type { Attributes, Child } from './solid.ts';

export interface IconButtonProps extends Attributes {
  /** The icon to show. */
  readonly icon: IconName;
  /** Required accessible name; also the tooltip. */
  readonly label: string;
  /** Makes it a toggle (aria-pressed). Leave undefined for a plain button. */
  readonly pressed?: boolean | undefined;
  /** Bordered box for standalone buttons. */
  readonly outlined?: boolean | undefined;
  /** 28 / 36 / 40 px square (each held to the 44px target, decision 71). */
  readonly size?: 'sm' | 'md' | 'lg' | undefined;
  /** danger turns the icon red on hover. */
  readonly tone?: 'default' | 'danger' | undefined;
  /** Renders an <a>. */
  readonly href?: string | undefined;
  /** Tooltip text; defaults to label. false removes it. */
  readonly title?: string | false | undefined;
  /** Button type, default "button". */
  readonly type?: 'button' | 'submit' | 'reset' | undefined;
  /** Extra class. */
  readonly class?: string | undefined;
  /** Extra content such as a badge dot. */
  readonly children?: Child;
}

export default function IconButton(props: IconButtonProps): Child {
  const p = mergeProps({ size: 'md', type: 'button', tone: 'default' } as const, props);
  const [local, rest] = splitProps(p, ['icon', 'label', 'pressed', 'outlined', 'size', 'tone', 'class', 'href', 'type', 'title', 'children']);

  const cls = (): string =>
    ['ui-icon-button', `ui-icon-button--${local.size}`, local.outlined === true && 'ui-icon-button--outlined', local.tone === 'danger' && 'ui-icon-button--danger', local.class]
      .filter(Boolean)
      .join(' ');

  const iconSize = (): 'sm' | 'md' | 'lg' => (local.size === 'lg' ? 'lg' : local.size === 'sm' ? 'sm' : 'md');
  const title = (): string | undefined => (local.title === false ? undefined : (local.title ?? local.label));
  const pressed = (): 'true' | 'false' | undefined => (local.pressed === undefined ? undefined : local.pressed ? 'true' : 'false');
  const icon = (): Child =>
    component(Icon, {
      get name() {
        return local.icon;
      },
      get size() {
        return iconSize();
      },
    });

  return when(
    () => local.href !== undefined && local.href !== '',
    () => el('a', spread(rest, { href: () => local.href, class: cls, 'aria-label': () => local.label, title }), icon(), () => local.children),
    () =>
      el(
        'button',
        spread(rest, { type: () => local.type, class: cls, 'aria-label': () => local.label, 'aria-pressed': pressed, title }),
        icon(),
        () => local.children,
      ),
  );
}
